import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Button,
  Banner,
  Badge,
  List,
  Modal,
  FormLayout,
  Select,
  Divider,
  Icon
} from '@shopify/polaris';
import { 
  CreditCardMajor, 
  RefreshMajor
} from '@shopify/polaris-icons';
import { useAuth } from '../hooks/useAuth';
import { 
  useShopifyBilling, 
  SHOPIFY_BILLING_PLANS, 
  formatCurrency,
  isTestMode
} from '../services/shopifyBilling';

interface Subscription {
  id: string;
  status: 'active' | 'canceled' | 'past_due' | 'unpaid' | 'trialing' | 'no_subscription';
  plan_type: 'starter' | 'professional' | 'unlimited';
  current_period_start: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  stripe_subscription_id: string;
  stripe_customer_id: string;
}

// Using ShopifyBillingPlan interface and SHOPIFY_BILLING_PLANS from shopifyBilling service

export const Billing: React.FC = () => {
  const { user: _user } = useAuth();
  const billingService = useShopifyBilling();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>('professional');
  const [isProcessing, setIsProcessing] = useState(false);
  const [testMode] = useState(isTestMode());

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      const subscriptionData = await billingService.getSubscriptionStatus();
      setSubscription({
        id: 'sub_123',
        status: subscriptionData.status,
        plan_type: subscriptionData.plan as 'starter' | 'professional' | 'unlimited',
        current_period_start: new Date().toISOString(),
        current_period_end: subscriptionData.currentPeriodEnd,
        cancel_at_period_end: subscriptionData.cancelAtPeriodEnd,
        stripe_subscription_id: 'sub_123',
        stripe_customer_id: 'cus_123'
      });
    } catch (error) {
      console.error('Failed to load subscription:', error);
    }
  };

  const handleUpgrade = async () => {
    setIsProcessing(true);
    try {
      await billingService.upgradeSubscription(selectedPlan);
      setShowUpgradeModal(false);
    } catch (error) {
      console.error('Failed to upgrade:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = async () => {
    setIsProcessing(true);
    try {
      await billingService.cancelSubscription();
      setShowCancelModal(false);
      await loadSubscription(); // Reload subscription data
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTestCharge = async () => {
    try {
      // TODO: Implement test charge
      // await billingService.createTestCharge(selectedPlan);
      setShowTestModal(false);
      // Show success message
    } catch (error) {
      console.error('Failed to create test charge:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge tone="success">Active</Badge>;
      case 'canceled':
        return <Badge tone="critical">Canceled</Badge>;
      case 'past_due':
        return <Badge tone="warning">Past Due</Badge>;
      case 'unpaid':
        return <Badge tone="critical">Unpaid</Badge>;
      case 'trialing':
        return <Badge tone="info">Trial</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getCurrentPlan = () => {
    return SHOPIFY_BILLING_PLANS.find(plan => plan.id === subscription?.plan_type) || SHOPIFY_BILLING_PLANS[0];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Using imported formatCurrency from shopifyBilling service

  return (
    <Page title="Billing">
      <Layout>
        {/* Test Mode Banner */}
        {testMode && (
          <Layout.Section>
            <Banner title="Test Mode Active" tone="info">
              <p>You are currently in test mode. All billing operations will create test charges that won't result in actual payments. 
              This is perfect for testing your app's billing system before going live.</p>
              <p><strong>To test subscription payments:</strong></p>
              <List type="bullet">
                <List.Item>Ensure Shopify Payments is enabled in test mode on your development store</List.Item>
                <List.Item>Use test credit card numbers from Shopify's documentation</List.Item>
                <List.Item>Complete the billing flow to test the full subscription process</List.Item>
              </List>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          {subscription ? (
            <Card>
              <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <div>
                    <Text variant="headingMd" as="h2">
                      Current Subscription
                    </Text>
                    <Text variant="bodyMd" as="p" tone="subdued">
                      Manage your TranslateFlow subscription and billing.
                    </Text>
                  </div>
                  {getStatusBadge(subscription.status)}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <Text variant="headingSm" as="h3">Plan Details</Text>
                    <Text variant="bodyMd" as="p" fontWeight="semibold">
                      {getCurrentPlan().name}
                    </Text>
                    <Text variant="bodyMd" as="p" tone="subdued">
                      {formatCurrency(getCurrentPlan().price, getCurrentPlan().currency)}/{getCurrentPlan().interval}
                    </Text>
                  </div>
                  <div>
                    <Text variant="headingSm" as="h3">Billing Period</Text>
                    <Text variant="bodyMd" as="p">
                      {formatDate(subscription.current_period_start)} - {subscription.current_period_end ? formatDate(subscription.current_period_end) : 'No end date'}
                    </Text>
                    {subscription.cancel_at_period_end && (
                      <Banner tone="warning">
                        Your subscription will be canceled at the end of the current billing period.
                      </Banner>
                    )}
                  </div>
                </div>

                <Divider />

                <div style={{ marginTop: '20px' }}>
                  <Text variant="headingSm" as="h3">Plan Features</Text>
                  <List type="bullet">
                    {getCurrentPlan().features.map((feature, index) => (
                      <List.Item key={index}>{feature}</List.Item>
                    ))}
                  </List>
                </div>

                <div style={{ marginTop: '20px', display: 'flex', gap: '12px' }}>
                  {subscription.plan_type !== 'unlimited' && (
                    <Button variant="primary" onClick={() => setShowUpgradeModal(true)}>
                      Upgrade Plan
                    </Button>
                  )}
                  {subscription.status === 'active' && !subscription.cancel_at_period_end && (
                    <Button onClick={() => setShowCancelModal(true)}>
                      Cancel Subscription
                    </Button>
                  )}
                  {testMode && (
                    <Button onClick={() => setShowTestModal(true)}>
                      Create Test Charge
                    </Button>
                  )}
                  <Button onClick={loadSubscription} icon={RefreshMajor}>
                    Refresh
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <Card>
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <Icon source={CreditCardMajor} />
                <div style={{ marginTop: '16px' }}>
                  <Text variant="headingMd" as="h2">
                    No Active Subscription
                  </Text>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <Text variant="bodyMd" as="p" tone="subdued">
                    Choose a plan to start translating your Shopify content.
                  </Text>
                </div>
                <Button variant="primary" onClick={() => setShowUpgradeModal(true)}>
                  Choose a Plan
                </Button>
              </div>
            </Card>
          )}
        </Layout.Section>

        <Layout.Section>
          <Card>
            <div style={{ padding: '20px' }}>
              <Text variant="headingMd" as="h2">
                Available Plans
              </Text>
              <div style={{ marginTop: '16px' }}>
                {SHOPIFY_BILLING_PLANS.map((plan) => (
                  <div key={plan.id} style={{ 
                    border: '1px solid #e1e3e5', 
                    borderRadius: '8px', 
                    padding: '16px', 
                    marginBottom: '12px',
                    backgroundColor: subscription?.plan_type === plan.id ? '#f6f6f7' : 'white'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <Text variant="headingSm" as="h3">{plan.name}</Text>
                      <Text variant="headingMd" as="p" fontWeight="semibold">
                        {formatCurrency(plan.price, plan.currency)}/month
                      </Text>
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <List type="bullet">
                        {plan.features.slice(0, 3).map((feature, index) => (
                          <List.Item key={index}>{feature}</List.Item>
                        ))}
                      </List>
                    </div>
                    {subscription?.plan_type === plan.id ? (
                      <Badge tone="success">Current Plan</Badge>
                    ) : (
                      <Button size="slim" onClick={() => {
                        setSelectedPlan(plan.id);
                        setShowUpgradeModal(true);
                      }}>
                        {subscription ? 'Switch to Plan' : 'Choose Plan'}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Upgrade Modal */}
      <Modal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Upgrade Subscription"
        primaryAction={{
          content: testMode ? 'Create Test Charge' : 'Upgrade Now',
          onAction: handleUpgrade,
          loading: isProcessing
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowUpgradeModal(false)
          }
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <Select
              label="Select Plan"
              options={SHOPIFY_BILLING_PLANS.map(plan => ({
                label: `${plan.name} - ${formatCurrency(plan.price, plan.currency)}/month`,
                value: plan.id
              }))}
              value={selectedPlan}
              onChange={setSelectedPlan}
            />
            <div style={{ marginTop: '16px' }}>
              <Text variant="headingSm" as="h3">Plan Features</Text>
              <List type="bullet">
                {SHOPIFY_BILLING_PLANS.find(p => p.id === selectedPlan)?.features.map((feature, index) => (
                  <List.Item key={index}>{feature}</List.Item>
                ))}
              </List>
            </div>
            {testMode && (
              <Banner tone="info">
                <p>This will create a test charge. No actual payment will be processed.</p>
              </Banner>
            )}
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Cancel Modal */}
      <Modal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Cancel Subscription"
        primaryAction={{
          content: 'Cancel Subscription',
          onAction: handleCancel,
          loading: isProcessing,
          destructive: true
        }}
        secondaryActions={[
          {
            content: 'Keep Subscription',
            onAction: () => setShowCancelModal(false)
          }
        ]}
      >
        <Modal.Section>
          <Banner tone="warning">
            Are you sure you want to cancel your subscription? You'll continue to have access until the end of your current billing period.
          </Banner>
          <div style={{ marginTop: '16px' }}>
            <Text variant="bodyMd" as="p">
              Your subscription will be canceled at the end of the current billing period on{' '}
              <strong>{subscription && subscription.current_period_end ? formatDate(subscription.current_period_end) : 'No end date'}</strong>.
            </Text>
          </div>
        </Modal.Section>
      </Modal>

      {/* Test Charge Modal */}
      <Modal
        open={showTestModal}
        onClose={() => setShowTestModal(false)}
        title="Create Test Charge"
        primaryAction={{
          content: 'Create Test Charge',
          onAction: handleTestCharge,
          loading: isProcessing
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowTestModal(false)
          }
        ]}
      >
        <Modal.Section>
          <Banner tone="info">
            <p>This will create a test charge for the {SHOPIFY_BILLING_PLANS.find(p => p.id === selectedPlan)?.name}. 
            No actual payment will be processed.</p>
          </Banner>
          <div style={{ marginTop: '16px' }}>
            <Text variant="bodyMd" as="p">
              Test charges are perfect for validating your app's billing system before going live.
            </Text>
          </div>
        </Modal.Section>
      </Modal>
    </Page>
  );
}; 